// ABOUTME: fetch wrappers for the bulk change (find & replace) REST routes under /api/find-replace.
// ABOUTME: Covers options, scope preview, scan/apply/cancel, runs, rows, delete impact, exclusions and saved rules; failures throw FindReplaceApiError.

import {
    BulkChangeRuleDto,
    BulkChangeScope,
    CreateRunResponse,
    DeleteImpactDto,
    ExclusionsRequest,
    FindReplaceOptions,
    FindReplaceRowsResponse,
    FindReplaceRunDto,
    RowsFilter,
    RowsPaging,
    RuleBody,
    RuleRunSummary,
    RunInput,
    ScopePreview,
    StartApplyResponse,
} from '../types';

const BASE_URL = '/api/find-replace';

/** A non-2xx answer of the find & replace API; `status` is the HTTP status (404, 409...). */
export class FindReplaceApiError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = 'FindReplaceApiError';
        this.status = status;
    }
}

const readErrorMessage = async (response: Response): Promise<string> => {
    try {
        const body = await response.json();
        return body?.message ?? body?.error ?? `HTTP ${response.status}`;
    } catch {
        return response.statusText || `HTTP ${response.status}`;
    }
};

const ensureOk = async (response: Response): Promise<void> => {
    if (!response.ok) {
        throw new FindReplaceApiError(response.status, await readErrorMessage(response));
    }
};

const runUrl = (runId: string) => `${BASE_URL}/runs/${encodeURIComponent(runId)}`;

export async function getFindReplaceOptions(hierarchyId: string): Promise<FindReplaceOptions> {
    const params = new URLSearchParams({ hierarchyId });
    const response = await fetch(`${BASE_URL}/options?${params.toString()}`, { method: 'GET' });
    await ensureOk(response);
    return response.json();
}

export async function getFindReplaceRun(runId: string): Promise<FindReplaceRunDto> {
    const response = await fetch(runUrl(runId), { method: 'GET' });
    await ensureOk(response);
    return response.json();
}

export async function getDeleteImpact(runId: string): Promise<DeleteImpactDto> {
    const response = await fetch(`${runUrl(runId)}/delete-impact`, { method: 'GET' });
    await ensureOk(response);
    return response.json();
}

export async function getFindReplaceRows(
    runId: string,
    filter: RowsFilter,
    paging: RowsPaging,
): Promise<FindReplaceRowsResponse> {
    const params = new URLSearchParams();
    if (filter.status) params.set('status', filter.status);
    if (filter.actionIndex !== undefined) params.set('actionIndex', `${filter.actionIndex}`);
    if (filter.ctg) params.set('ctg', filter.ctg);
    if (filter.ct) params.set('ct', filter.ct);
    if (filter.lang) params.set('lang', filter.lang);
    if (filter.excluded !== undefined) params.set('excluded', `${filter.excluded}`);
    if (filter.outcome) params.set('outcome', filter.outcome);
    if (filter.q) params.set('q', filter.q);
    params.set('skip', `${paging.skip}`);
    params.set('limit', `${paging.limit}`);

    const response = await fetch(`${runUrl(runId)}/rows?${params.toString()}`, {
        method: 'GET',
    });
    await ensureOk(response);
    const data: FindReplaceRowsResponse = await response.json();
    return { rows: data?.rows ?? [], count: data?.count ?? 0 };
}

export async function patchFindReplaceExclusions(
    runId: string,
    body: ExclusionsRequest,
): Promise<{ updated: number }> {
    const response = await fetch(`${runUrl(runId)}/exclusions`, {
        method: 'PATCH',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    await ensureOk(response);
    return response.json();
}

export async function deleteFindReplaceRun(runId: string): Promise<void> {
    const response = await fetch(runUrl(runId), { method: 'DELETE' });
    await ensureOk(response);
}

/** The count and a page of the nodes of a scope, before any run exists. */
export async function previewScope(
    scope: BulkChangeScope,
    paging: RowsPaging,
    signal?: AbortSignal,
): Promise<ScopePreview> {
    const params = new URLSearchParams({ skip: `${paging.skip}`, limit: `${paging.limit}` });
    const response = await fetch(`${BASE_URL}/scope-preview?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify({ scope }),
        signal,
    });
    await ensureOk(response);
    return response.json();
}

/** Starts the scan job of a new run. */
export async function createFindReplaceRun(input: RunInput): Promise<CreateRunResponse> {
    const response = await fetch(`${BASE_URL}/runs`, {
        method: 'POST',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify(input),
    });
    await ensureOk(response);
    return response.json();
}

/** Starts the apply job of a `ready` run. */
export async function startFindReplaceApply(
    runId: string,
    reapprovePublish: boolean,
): Promise<StartApplyResponse> {
    const response = await fetch(`${runUrl(runId)}/apply`, {
        method: 'POST',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify({ reapprovePublish }),
    });
    await ensureOk(response);
    return response.json();
}

/** Asks the scan or apply job of the run to stop. */
export async function cancelFindReplaceRun(runId: string): Promise<void> {
    const response = await fetch(`${runUrl(runId)}/cancel`, { method: 'POST' });
    await ensureOk(response);
}

const ruleUrl = (ruleId: string) => `${BASE_URL}/rules/${encodeURIComponent(ruleId)}`;

export async function listRules(): Promise<BulkChangeRuleDto[]> {
    const response = await fetch(`${BASE_URL}/rules`, { method: 'GET' });
    await ensureOk(response);
    return response.json();
}

export async function getRule(ruleId: string): Promise<BulkChangeRuleDto> {
    const response = await fetch(ruleUrl(ruleId), { method: 'GET' });
    await ensureOk(response);
    return response.json();
}

/** Creates the rule without an id, updates it otherwise. */
export async function saveRule(body: RuleBody, ruleId?: string): Promise<BulkChangeRuleDto> {
    const response = await fetch(ruleId ? ruleUrl(ruleId) : `${BASE_URL}/rules`, {
        method: ruleId ? 'PUT' : 'POST',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    await ensureOk(response);
    return response.json();
}

export async function deleteRule(ruleId: string): Promise<void> {
    const response = await fetch(ruleUrl(ruleId), { method: 'DELETE' });
    await ensureOk(response);
}

/** Launches the rule's job: it scans and stops at `ready` for review. */
export async function runRuleNow(ruleId: string): Promise<StartApplyResponse> {
    const response = await fetch(`${ruleUrl(ruleId)}/run`, { method: 'POST' });
    await ensureOk(response);
    return response.json();
}

export async function listRuleRuns(ruleId: string): Promise<RuleRunSummary[]> {
    const response = await fetch(`${ruleUrl(ruleId)}/runs`, { method: 'GET' });
    await ensureOk(response);
    return response.json();
}
